'use client';

import { useMemo } from 'react';

export default class CacheService implements Cache {
	private _cache?: Cache;
	private readonly _cacheName: string;

	constructor(cacheName: string) {
		this._cacheName = cacheName;
	}

	private async getCache() {
		if (!this._cache) {
			this._cache = await caches.open(this._cacheName);
		}
		return this._cache;
	}

	public async add(request: RequestInfo | URL): Promise<void> {
		const cache = await this.getCache();
		return cache.add(request);
	}

	public async addAll(requests: RequestInfo[]): Promise<void> {
		const cache = await this.getCache();
		return cache.addAll(requests);
	}

	public async delete(request: RequestInfo | URL, options?: CacheQueryOptions): Promise<boolean> {
		const cache = await this.getCache();
		return cache.delete(request, options);
	}

	public async keys(request?: RequestInfo | URL, options?: CacheQueryOptions): Promise<ReadonlyArray<Request>> {
		const cache = await this.getCache();
		return cache.keys(request, options);
	}

	public async match(request: RequestInfo | URL, options?: CacheQueryOptions): Promise<Response | undefined> {
		const cache = await this.getCache();
		return cache.match(request, options);
	}

	public async matchAll(request?: RequestInfo | URL, options?: CacheQueryOptions): Promise<ReadonlyArray<Response>> {
		const cache = await this.getCache();
		return cache.matchAll(request, options);
	}

	public async put(request: RequestInfo | URL, response: Response): Promise<void> {
		const cache = await this.getCache();
		return cache.put(request, response);
	}
}

export function useCacheService(cacheName: string) {
	return useMemo(() => new CacheService(cacheName), [cacheName]);
}
